import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { questions } from '../../data/questions';
import { zones } from '../../data/zones';
interface ResultsScreenProps {
  answers: (boolean | null)[];
  onReset: () => void;
}
export function ResultsScreen({
  answers,
  onReset
}: ResultsScreenProps) {
  const [displayScore, setDisplayScore] = useState(0);
  const score = answers.filter(a => a === true).length;
  const zone = zones.find(z => score >= z.min && score <= z.max) || zones[0];
  useEffect(() => {
    let current = 0;
    if (score === 0) return;
    const interval = setInterval(() => {
      current++;
      setDisplayScore(current);
      if (current >= score) {
        clearInterval(interval);
      }
    }, 120);
    return () => clearInterval(interval);
  }, [score]);
  const getScoreColor = (s: number) => {
    if (s <= 3) return 'var(--red)';
    if (s <= 6) return 'var(--orange)';
    if (s <= 8) return 'var(--gold)';
    return 'var(--green)';
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="py-10 px-5 min-h-screen text-center" data-id="element-62">
      <div className="max-w-[680px] mx-auto" data-id="element-63">
        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.2
      }} className="text-[var(--gold)] text-xs tracking-[4px] font-bold uppercase mb-2" data-id="element-64">
          TU RESULTADO
        </motion.div>
        <motion.div initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        delay: 0.4,
        type: 'spring'
      }} className="text-[72px] font-extrabold my-5 leading-none transition-colors duration-300" style={{
        color: getScoreColor(displayScore)
      }} data-id="element-65">
          {displayScore}
        </motion.div>
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6
      }} className="text-[var(--gray)] text-lg mb-8" data-id="element-66">
          de 10
        </motion.div>

        {/* Zone Card */}
        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.8
      }} className="p-8 rounded-xl my-8 text-left" style={{
        backgroundColor: zone.bg,
        border: `1px solid ${zone.color}`
      }} data-id="element-67">
          <div className="text-sm font-bold tracking-[2px] mb-3 uppercase" style={{
          color: zone.color
        }} data-id="element-68">
            {zone.label}
          </div>
          <div className="text-sm leading-[1.7] opacity-90 text-white" data-id="element-69">
            {zone.desc}
          </div>
        </motion.div>

        {/* Breakdown */}
        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 1
      }} className="text-left my-8 bg-[#0d0d0d] rounded-xl p-6 md:p-8" data-id="element-70">
          <h3 className="text-sm text-[var(--gold)] tracking-[2px] mb-4 font-bold uppercase" data-id="element-71">
            DETALLE POR PREGUNTA
          </h3>
          <div className="flex flex-col" data-id="element-72">
            {questions.map((q, i) => {
            const isYes = answers[i] === true;
            return <div key={i} className="flex justify-between items-center py-3 border-b border-[#1a1a1a] last:border-b-0 text-[13px]" data-id="element-73">
                  <span className="flex items-center text-white" data-id="element-74">
                    <span className="inline-flex items-center justify-center w-[22px] h-[22px] text-[10px] font-bold bg-[var(--navy)] rounded-full mr-3 shrink-0" data-id="element-75">
                      {i + 1}
                    </span>
                    {q.area}
                  </span>
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ml-4 ${isYes ? 'bg-[var(--green)]' : 'bg-[var(--red)]'}`} data-id="element-76" />
                </div>;
          })}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 1.2
      }} className="mt-10 p-8 rounded-xl border border-[var(--gold)] bg-gradient-to-br from-[#0A1628] to-[#1a2a40]" data-id="element-77">
          <h2 className="text-[var(--gold)] text-lg font-bold mb-3" data-id="element-78">
            ¿Tu puntaje reveló brechas?
          </h2>
          <p className="text-[#cccccc] text-sm leading-[1.6] mb-6" data-id="element-79">
            No es falta de esfuerzo. Es falta de arquitectura. BTS™ Método
            Arquitecto instala en 90 días la infraestructura completa para que
            tu agencia reclute, active y retenga agentes con sistema propio.
          </p>
          <motion.div whileHover={{
          scale: 1.05,
          boxShadow: '0 0 30px rgba(212,168,67,0.3)'
        }} whileTap={{
          scale: 0.95
        }} className="inline-block" data-id="element-80">
            <Link to="/contact-us" className="inline-block bg-[var(--gold)] text-[var(--black)] py-4 px-8 rounded-lg font-bold text-[15px] no-underline transition-all" data-id="element-81">
              AGENDA TU LLAMADA — 25 MIN
            </Link>
          </motion.div>
          <p className="text-[#666666] text-[11px] mt-4" data-id="element-82">
            Sin compromiso. Te mostramos el sistema completo.
          </p>
        </motion.div>

        <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5
      }} onClick={onReset} className="inline-block mt-8 text-[var(--gray)] text-xs cursor-pointer underline bg-transparent border-none hover:text-white transition-colors" data-id="element-83">
          Volver a hacer la auditoría
        </motion.button>
      </div>
    </motion.div>;
}